import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { query, where, getDocs } from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { daysSince } from "../../utils/reports/format";

/**
 * Open Work Orders (aging) — all incomplete work orders by age, with room,
 * status, assignee, and PTE-approved flag. Sorted oldest-first.
 */
export default function GenerateOpenWorkOrdersAging({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const ref = propertyCollection(selectedClient, selectedProperty, "workOrders");
      const snap = await getDocs(query(ref, where("completed", "==", false)));

      const orders = snap.docs
        .map((d) => {
          const wo = d.data();
          return {
            room: wo.room ?? "—",
            details: wo.orderDetails || "—",
            status: wo.status || "pending",
            assignedTo: wo.assignedTo || "Unassigned",
            pte: wo.pteApproved ? "Yes" : "No",
            age: daysSince(wo.createdAt || wo.reportedTime),
          };
        })
        .sort((a, b) => (b.age ?? -1) - (a.age ?? -1));

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Open Work Orders (Aging)",
        dateRange: `${orders.length} open`,
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const body = orders.map((o) => [
        String(o.room),
        o.details,
        o.status,
        o.assignedTo,
        o.pte,
        o.age == null ? "—" : String(o.age),
      ]);

      if (body.length === 0) {
        body.push([{ content: "No open work orders.", colSpan: 6, styles: { fontStyle: "italic" } }]);
      }

      autoTable(pdf, {
        startY: contentTop,
        head: [["Room", "Details", "Status", "Assigned", "PTE", "Age (d)"]],
        body,
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, overflow: "linebreak" },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: "auto" },
          4: { cellWidth: 14, halign: "center" },
          5: { cellWidth: 18, halign: "right" },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Open_Work_Orders_Aging", { ext: "pdf" })
      );
    } catch (e) {
      console.error("Error generating Open Work Orders Aging:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generate}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "Open Work Orders (Aging)"}
    </Button>
  );
}
